import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Tag, seg } from '../../tags/Tag';
import type { CoreTagColor } from '../../tags/Tag';
import { Launch, ChevronRight } from '@core/icons-react';
import { DropdownContainer } from '../shared';
import { DropdownFooter } from '../shared/components/DropdownFooter';
import type { NestedOption } from '../shared/types';

const cx = (...classes: (string | boolean | undefined)[]) => 
  classes.filter(Boolean).join(' ');

export interface DropdownOption {
  id: string;
  label?: string; // Optional for value/unit format options
  parent?: string; // Optional parent text (e.g., application name for operations)
  description?: string; // Optional secondary text used for search/filter
  disabled?: boolean;
  tag?: {
    label: string;
    variant?: CoreTagColor | 'secondary' | 'primary';
    style?: React.CSSProperties;
  };
  openable?: boolean;  // Whether this item can be opened (shows launch icon)
  onOpen?: () => void; // Handler for opening this item
  icon?: React.ReactNode; // Icon to show on the right for view-only items
  children?: NestedOption[]; // Nested options for expandable items
  value?: string; // Numeric value to display on left (for unit selection dropdowns)
  unit?: string;  // Unit string to display on right with semibold weight
}

export interface OptionDropdownProps {
  isOpen: boolean;
  onClose?: () => void;
  options: DropdownOption[];
  selectedItem?: string;
  selectedItems?: string[];
  onSelectionChange?: (id: string) => void;
  onMultiSelectionChange?: (ids: string[]) => void;
  multiSelect?: boolean;
  position?: 'left' | 'right';
  direction?: 'down' | 'up';
  size?: 'small' | 'medium';
  borderRadius?: 'full' | 'top' | 'bottom';
  minWidth?: string;
  maxWidth?: string;
  maxHeight?: string;
  anchorRef?: React.RefObject<HTMLElement>;
  className?: string;
  showCheckmarks?: boolean;
  renderFooter?: boolean;
  variant?: 'subtle' | 'default' | 'strong' | 'glassmorphic';
  showStateTags?: boolean;
  requireSelection?: boolean; // When true, shows "Reset" instead of "Unselect"
  defaultValue?: string; // Value to reset to when requireSelection is true
  readOnly?: boolean; // When true, disables selection and footer buttons
  openableMode?: boolean; // When true, items show launch icons instead of checkmarks
  readOnlyAction?: {
    label: string;
    onClick: () => void;
  }; // Custom action for readonly mode footer
  offsetY?: number; // Vertical offset from anchor element
  zIndex?: number; // Custom z-index for stacking control
  emptyMessage?: string; // Message to display when there are no options
  title?: string; // Optional title/header text displayed at the top
  /** Show search input and filter options by label/description */
  searchable?: boolean;
  /** Placeholder for the search input */
  searchPlaceholder?: string;
  /**
   * When provided, the dropdown runs in server-side search mode: it emits the
   * typed term instead of filtering `options` locally. Debounce upstream.
   */
  onSearchChange?: (term: string) => void;
}

export function OptionDropdown({
  isOpen,
  onClose,
  options,
  selectedItem,
  selectedItems = [],
  onSelectionChange,
  onMultiSelectionChange,
  multiSelect = false,
  position = 'left',
  direction = 'down',
  size = 'medium',
  borderRadius = 'full',
  minWidth = '200px',
  maxWidth = '20rem',
  maxHeight = '20rem',
  anchorRef,
  className,
  showCheckmarks = true,
  renderFooter = true,
  variant = 'default',
  showStateTags = false,
  requireSelection = false,
  defaultValue = '',
  readOnly = false,
  openableMode = false,
  readOnlyAction,
  offsetY,
  zIndex,
  emptyMessage = 'No options available',
  title,
  searchable = false,
  searchPlaceholder = 'Search...',
  onSearchChange
}: OptionDropdownProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredParentWithChildren, setHoveredParentWithChildren] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const optionRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const serverSide = typeof onSearchChange === 'function';

  React.useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      if (serverSide) onSearchChange!('');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredOptions = React.useMemo(() => {
    // Server-side mode: parent owns filtering, render options as received.
    if (serverSide) return options;
    if (!searchable || !searchTerm.trim()) return options;
    const q = searchTerm.toLowerCase().trim();
    return options.filter(
      (opt) =>
        (opt.label?.toLowerCase().includes(q)) ||
        (opt.description?.toLowerCase().includes(q)) ||
        (opt.parent?.toLowerCase().includes(q))
    );
  }, [options, searchable, searchTerm]);

  const handleMouseEnterOption = (option: DropdownOption) => {
    setHoveredItem(option.id);
    
    if (option.children && option.children.length > 0) {
      // Clear any pending timeout
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
      // Small delay before opening submenu
      submenuTimeoutRef.current = setTimeout(() => {
        setHoveredParentWithChildren(option.id);
      }, 150);
    } else {
      // Close submenu if hovering non-parent item
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
      setHoveredParentWithChildren(null);
    }
  };

  const handleMouseLeaveOption = () => {
    setHoveredItem(null);
  };

  const handleMouseLeaveSubmenu = () => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
    submenuTimeoutRef.current = setTimeout(() => {
      setHoveredParentWithChildren(null);
    }, 150);
  };

  const handleMouseEnterSubmenu = () => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current);
    }
  };
  
  const handleOptionClick = (option: DropdownOption, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (option.disabled) return;
    
    // In openable mode, trigger the open handler
    if (openableMode) {
      if (option.openable && option.onOpen) {
        option.onOpen();
        onClose?.();
      }
      return;
    }
    
    if (readOnly) return;
    
    if (multiSelect) {
      const currentSelected = selectedItems || [];
      let newSelected: string[];
      
      if (currentSelected.includes(option.id)) {
        // Remove from selection
        newSelected = currentSelected.filter(id => id !== option.id);
      } else {
        // Add to selection
        newSelected = [...currentSelected, option.id];
      }
      
      onMultiSelectionChange?.(newSelected);
    } else {
      // Single select
      onSelectionChange?.(option.id);
      onClose?.();
    }
  };
  
  const handleSelectAll = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const allIds = filteredOptions
      .filter(opt => !opt.disabled)
      .map(opt => opt.id);
    onMultiSelectionChange?.(allIds);
  };
  
  const handleClearAll = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onMultiSelectionChange?.([]);
  };
  
  // Calculate submenu position based on dropdown container and hovered option
  const getSubmenuStyle = (optionId: string): React.CSSProperties => {
    const optionEl = optionRefs.current.get(optionId);
    const containerEl = containerRef.current?.closest('.core-dropdown-container') as HTMLElement;
    if (!optionEl || !containerEl) return {};
    
    const optionRect = optionEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    
    return {
      top: optionRect.top,
      left: containerRect.right + 4, // 0.25rem spacing
      maxHeight
    };
  };

  const dropdownClasses = cx(
    'core-option-dropdown',
    `core-option-dropdown--${position}`,
    variant && `core-option-dropdown--${variant}`,
    className
  );
  
  return (
    <DropdownContainer
      isOpen={isOpen}
      onClose={onClose}
      anchorRef={anchorRef}
      position={position}
      direction={direction}
      variant={variant}
      size={size}
      borderRadius={borderRadius}
      minWidth={minWidth}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      className={dropdownClasses}
      onClick={(e) => e.stopPropagation()}
      offsetY={offsetY}
      zIndex={zIndex}
    >
      {/* Header with title */}
      {title && (
        <div className="core-option-dropdown__header">
          {title}
        </div>
      )}

      {/* Search input */}
      {searchable && (
        <div className="core-option-dropdown__search">
          <input
            type="text"
            className="core-option-dropdown__search-input"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (serverSide) onSearchChange!(e.target.value);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            aria-label={searchPlaceholder}
          />
        </div>
      )}
      
      {/* Scrollable options container */}
      <div 
        ref={containerRef}
        className="core-option-dropdown__container"
        style={{ 
          maxHeight: renderFooter && !readOnly 
            ? `calc(${maxHeight} - 3.5rem${title ? ' - 2.5rem' : ''}${searchable ? ' - 2.5rem' : ''})` 
            : title || searchable
              ? `calc(${maxHeight} - 2.5rem${title && searchable ? ' - 2.5rem' : ''})` 
              : maxHeight 
        }}
        onScroll={(e) => e.stopPropagation()}
      >
        {filteredOptions.length === 0 ? (
          <div className="core-option-dropdown__placeholder">
            {emptyMessage}
          </div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = multiSelect 
              ? selectedItems?.includes(option.id) || false
              : option.id === selectedItem;
            const hasChildren = option.children && option.children.length > 0;
            const isSubmenuOpen = hoveredParentWithChildren === option.id;
            
            const optionClasses = cx(
              'core-option-dropdown__option',
              isSelected && 'core-option-dropdown__option--selected',
              option.disabled && 'core-option-dropdown__option--disabled',
              openableMode && option.openable && 'core-option-dropdown__option--openable',
              hasChildren && 'core-option-dropdown__option--has-children',
              isSubmenuOpen && 'core-option-dropdown__option--submenu-open'
            );
            
            return (
              <div 
                key={option.id} 
                className="core-option-dropdown__option-wrapper"
                style={{ position: 'relative' }}
              >
                <button
                  ref={(el) => {
                    if (el) optionRefs.current.set(option.id, el);
                  }}
                  className={optionClasses}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasChildren) {
                      setHoveredParentWithChildren(prev => 
                        prev === option.id ? null : option.id
                      );
                    } else {
                      handleOptionClick(option, e);
                    }
                  }}
                  disabled={option.disabled}
                  onMouseEnter={() => handleMouseEnterOption(option)}
                  onMouseLeave={handleMouseLeaveOption}
                >
                  {/* Value/Unit format for unit selection dropdowns */}
                  {option.value !== undefined ? (
                    <>
                      <span className="core-option-dropdown__value">{option.value}</span>
                      {option.unit !== undefined && (
                        <span className="core-option-dropdown__unit">{option.unit}</span>
                      )}
                    </>
                  ) : (
                    <>
                  <div className="core-option-dropdown__label-container">
                        <span className="core-option-dropdown__label">{option.label || ''}</span>
                    {option.parent && (
                      <span className="core-option-dropdown__parent">{option.parent}</span>
                    )}
                  </div>
                  {showStateTags && option.tag && (
                    <Tag
                      segments={[seg.text(option.tag.label)]}
                      color={(option.tag.variant === 'secondary' ? 'neutral' : option.tag.variant === 'primary' ? 'info' : option.tag.variant) as CoreTagColor || 'neutral'}
                      shape="rect"
                      border="none"
                      size="xxs"
                      style={{ marginLeft: 'auto', ...option.tag.style }}
                    />
                  )}
                  {/* Show right chevron for items with children */}
                  {hasChildren ? (
                    <ChevronRight 
                      size={14} 
                      className="core-option-dropdown__submenu-icon"
                      style={{ marginLeft: showStateTags && option.tag ? '0.5rem' : 'auto' }}
                    />
                  ) : openableMode && option.openable && hoveredItem === option.id ? (
                    <Launch 
                      size={14} 
                      className="core-option-dropdown__launch-icon"
                      style={{ marginLeft: showStateTags && option.tag ? '0.5rem' : 'auto' }}
                    />
                  ) : readOnly && option.icon ? (
                    <span 
                      className="core-option-dropdown__icon"
                      style={{ marginLeft: showStateTags && option.tag ? '0.5rem' : 'auto' }}
                    >
                      {option.icon}
                    </span>
                      ) : option.icon ? (
                        <span 
                          className="core-option-dropdown__icon"
                          style={{ marginLeft: 'auto' }}
                        >
                          {option.icon}
                        </span>
                  ) : !openableMode && isSelected && showCheckmarks && !(showStateTags && option.tag) ? (
                    <CheckmarkIcon />
                  ) : null}
                    </>
                  )}
                </button>
                {/* Flyout submenu -- portaled to body to escape overflow:hidden + backdrop-filter containing block */}
                {hasChildren && isSubmenuOpen && typeof document !== 'undefined' && createPortal(
                  <div 
                    className={cx(
                      'core-option-dropdown__submenu',
                      `core-option-dropdown__submenu--${variant}`
                    )}
                    style={getSubmenuStyle(option.id)}
                    onMouseEnter={handleMouseEnterSubmenu}
                    onMouseLeave={handleMouseLeaveSubmenu}
                    onMouseDown={(e) => {
                      // Prevent the document-level mousedown handler in useDropdownLifecycle
                      // from treating submenu clicks as "outside" the dropdown
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                    }}
                  >
                    {option.children!.map((child) => {
                      const isChildSelected = child.id === selectedItem;
                      return (
                        <button
                          key={child.id}
                          className={cx(
                            'core-option-dropdown__option',
                            isChildSelected && 'core-option-dropdown__option--selected'
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectionChange?.(child.id);
                            onClose?.();
                          }}
                        >
                          <span className="core-option-dropdown__label" style={{ flex: 1 }}>{child.label}</span>
                          {child.tag && (
                            <Tag
                              template="http"
                              segments={[seg.text(child.tag.label)]}
                              color={(child.tag.variant || 'neutral') as CoreTagColor}
                              size="xxs"
                              style={{ flexShrink: 0 }}
                            />
                          )}
                          {isChildSelected && showCheckmarks && !child.tag && <CheckmarkIcon />}
                        </button>
                      );
                    })}
                  </div>,
                  document.body
                )}
              </div>
            );
          })
        )}
      </div>
      
      {/* Multi-select footer */}
      {multiSelect && renderFooter && !readOnly && !openableMode && filteredOptions.length > 0 && (
        <DropdownFooter
          variant={variant}
          actions={[
            { label: 'Select All', onClick: () => handleSelectAll() },
            { label: 'Clear All', onClick: () => handleClearAll() }
          ]}
        />
      )}
      
      {/* Single-select footer */}
      {!multiSelect && renderFooter && !readOnly && !openableMode && filteredOptions.length > 0 && (
        <DropdownFooter
          variant={variant}
          singleAction
          actions={[{
            label: requireSelection ? 'Reset' : 'Unselect',
            onClick: () => {
              onSelectionChange?.(requireSelection ? defaultValue : '');
              onClose?.();
            }
          }]}
        />
      )}
      
      {/* ReadOnly action footer */}
      {readOnly && renderFooter && readOnlyAction && (
        <DropdownFooter
          variant={variant}
          singleAction
          actions={[{
            label: readOnlyAction.label,
            onClick: () => {
              readOnlyAction.onClick();
              onClose?.();
            }
          }]}
        />
      )}
    </DropdownContainer>
  );
}

// Simple checkmark icon component
function CheckmarkIcon() {
  return (
    <svg 
      width="14" 
      height="14" 
      viewBox="0 0 16 16" 
      fill="currentColor"
      className="core-option-dropdown__checkmark"
    >
      <path d="M13.854 4.146a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 .708-.708L6.5 10.793l6.646-6.647a.5.5 0 0 1 .708 0z"/>
    </svg>
  );
}
